import cookies from 'next-cookies';
import Head from 'next/head';
import Link from 'next/link';
import React, { useState } from 'react';
import styled from 'styled-components';
import useSWR from 'swr';
import Cookies from 'universal-cookie';
import { fetcher } from '../../../api/bootstrap';
import { TidalButton } from '../../../components/common/button';

import { Box } from '../../../components/Flex';
import Loading from '../../../components/Loading';
import { ShopItemCard } from '../../../components/shop';
import TidalOrdersTable from '../../../components/shop/orders/TidalOrdersTable';
import { HeadingOne, Subheading } from '../../../components/Text';
import { COOKIE } from '../../../constants';
import useAuthOrRedirect from '../../../hooks/useAuthOrRedirect';
import {
  WHITE_LABEL_MARKET,
  WHITE_LABEL_TWITTER,
  WHITE_LABEL_URL,
} from '../../../lib/white-label';
import { ManageLink } from '../merchant/items';

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const ShopHeader = styled.div`
  display: grid;
  grid-template-columns: 8fr 2fr;
  gap: 0 8px;

  & > button {
    max-width: 160px;
    justify-self: flex-end;
  }
`;

const TidalOrdersTableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 0 25px;
  max-width: 100%;
  @media only screen and (max-width: 786px) {
    grid-gap: 8px 0;
  }
`;

const metaTitle = `Purchase digital items | ${WHITE_LABEL_MARKET}`;
const metaDescription = `Purchase digital items on ${WHITE_LABEL_MARKET}`;

const orderUrl = '/market/shop/orders';

function Orders({ fallbackData }) {
  const { data, error: err, mutate } = useSWR(`${orderUrl}`, { fallbackData });

  if (!useAuthOrRedirect()) return null;

  return (
    <Container>
      <Head>
        <title>Shop - {WHITE_LABEL_MARKET}</title>
        <meta property="og:url" content={`${WHITE_LABEL_URL}/shop`} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta
          property="og:image"
          content={`${process.env.BASE_URL}/_next/image?url=%2Fimages%2Fmeta.png&q=50&w=3840`}
        />
        <meta name="twitter:site" content={WHITE_LABEL_TWITTER} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta
          name="twitter:image"
          content={`${process.env.BASE_URL}/_next/image?url=%2Fimages%2Fmeta.png&w=3840&q=50`}
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>
      <TidalOrdersTableRow>
        <div className="flex w-full justify-between mt-0 md:mt-5">
          <HeadingOne>Shop Orders</HeadingOne>
        </div>

        <div className="mt-5 overflow-auto border rounded-lg max-h-1/2">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase "
                >
                  Order ID
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase "
                >
                  Date
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase "
                >
                  Item
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase "
                >
                  Order Total
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase "
                >
                  Order Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 ">
              {data?.orders.map((order) => (
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900 pr-2">
                        {order.id}
                      </div>
                      <Link href={`/shop/orders/${order.id}`} legacyBehavior>
                        <ManageLink>
                          <svg
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                            className='class="w-4 h-4 text-blue'
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                            ></path>
                          </svg>
                        </ManageLink>
                      </Link>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-800 whitespace-nowrap">
                    {order?.createdAt
                      ? new Date(order?.createdAt).toLocaleString()
                      : ''}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-800 whitespace-nowrap">
                    {order?.item?.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-800 whitespace-nowrap">
                    ${(order.orderTotal / 100).toFixed(2)}
                  </td>
                  <td
                    className={`px-6 py-4 text-sm ${
                      order.status === 'purchased'
                        ? 'text-green-500'
                        : 'text-gray-800'
                    } whitespace-nowrap font-bold`}
                  >
                    {order.status === 'purchased' ? 'Complete' : 'Pending'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TidalOrdersTableRow>

      <Box height={20} />
    </Container>
  );
}

export async function getServerSideProps(ctx) {
  const cookie = cookies(ctx).accessToken;
  try {
    const orders = await fetch(`${process.env.SHOP_BACKEND_URI}${orderUrl}`, {
      headers: {
        authorization: `Bearer ${cookie}`,
      },
    }).then((res) => res.json());

    return {
      props: {
        fallbackData: orders,
      },
    };
  } catch (error) {
    console.error(error);
    return {
      props: {
        fallbackData: [],
      },
    };
  }
}

export default Orders;
